import { useEffect, useMemo, useState } from "react";
import { formatComplianceScopeDisplay } from "../../compliance/complianceScopeDisplay.js";
import ProviderFrameworkPicker, {
  normalizeFrameworksForProviderSubmit,
} from "../onboarding/ProviderFrameworkPicker.jsx";
import { getDefaultProviderComplianceFrameworks } from "../../utils/providerFrameworks.js";
import { getProviderMeta, resolveProviderId } from "../../utils/providerContext.js";

export default function FrameworksModal({ open, onClose, project, onSaved }) {
  const providerId = resolveProviderId(project) || "okta";
  const fwMeta = getProviderMeta(providerId).frameworks;

  const initial = useMemo(() => {
    const v = project?.complianceFrameworks;
    if (Array.isArray(v) && v.length) return v.map((x) => String(x)).filter(Boolean);
    return getDefaultProviderComplianceFrameworks(providerId);
  }, [project?.complianceFrameworks, providerId]);

  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setValue(initial);
    setError("");
  }, [open, initial]);

  if (!open) return null;

  const baselineId = fwMeta?.baselineFrameworkId;
  const scopePreview = formatComplianceScopeDisplay(
    normalizeFrameworksForProviderSubmit(providerId, value),
    providerId,
  );

  const save = async () => {
    const projectId = project?.projectId;
    if (!projectId) return;
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          complianceFrameworks: normalizeFrameworksForProviderSubmit(providerId, value),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || "Save failed");
      onSaved?.(data.project);
      onClose?.();
    } catch (e) {
      setError(e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const providerLabel = getProviderMeta(providerId).displayName;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="frameworks-title"
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-[rgb(var(--idaxis-accent-primary-rgb)/0.2)] bg-[rgb(var(--idaxis-bg-primary-rgb)/0.55)] p-6 shadow-[0_0_40px_rgb(var(--idaxis-accent-primary-rgb)/0.12)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-500 hover:bg-white/5 hover:text-slate-300"
          aria-label="Close"
        >
          ✕
        </button>
        <h2 id="frameworks-title" className="text-lg font-semibold text-white">
          Compliance frameworks
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          {providerLabel} — this controls which findings are prioritized and which citations are shown.
        </p>
        <p className="mt-1 text-xs text-slate-500">Current scope: {scopePreview}</p>

        <div className="mt-5">
          <ProviderFrameworkPicker providerId={providerId} value={value} onChange={setValue} />
          {baselineId ? (
            <p className="mt-2 text-[11px] text-slate-500">{fwMeta?.emptySelectionHint}</p>
          ) : null}
        </div>

        {error ? <p className="mt-3 text-sm text-rose-400">{error}</p> : null}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={save}
            className="rounded-lg bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] px-4 py-2.5 text-sm font-semibold text-[var(--idaxis-bg-primary)] shadow-[0_0_16px_rgb(var(--idaxis-accent-primary-rgb)/0.25)] hover:shadow-[0_0_22px_rgb(var(--idaxis-accent-primary-rgb)/0.35)] disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
